import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const TYPE_TONE = {
  annual: "bg-emerald-100 text-emerald-900 border-emerald-300", sick: "bg-red-100 text-red-900 border-red-300",
  maternity_paternity: "bg-pink-100 text-pink-900 border-pink-300", unpaid: "bg-slate-200 text-slate-800 border-slate-300",
  compassionate: "bg-violet-100 text-violet-900 border-violet-300", other: "bg-amber-100 text-amber-900 border-amber-300",
};
const shift = (ym, d) => { const [y, m] = ym.split("-").map(Number); const dt = new Date(y, m - 1 + d, 1); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`; };
const label = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

/** Team leave calendar — approved (solid) and pending (dashed) leave per day. */
export function LeaveCalendar() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const { data } = useQuery({ queryKey: ["leave-calendar", month], queryFn: () => api.leaveCalendar(month) });
  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const offset = (new Date(y, m - 1, 1).getDay() + 6) % 7; // Monday first
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const rows = data?.rows || [];
  const holidays = Object.fromEntries((data?.holidays || []).map((h) => [h.date, h.name]));
  const forDay = (d) => {
    const iso = `${month}-${String(d).padStart(2, "0")}`;
    return rows.filter((r) => r.start_date <= iso && r.end_date >= iso);
  };
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="border border-border rounded-md bg-white p-4 mb-4" data-testid="leave-calendar">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-sm">Team calendar</h3>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="size-7" data-testid="leave-cal-prev" onClick={() => setMonth(shift(month, -1))}><ChevronLeft className="size-4" /></Button>
          <span className="text-sm font-medium w-36 text-center" data-testid="leave-cal-month">{label(month)}</span>
          <Button variant="ghost" size="icon" className="size-7" data-testid="leave-cal-next" onClick={() => setMonth(shift(month, 1))}><ChevronRight className="size-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] text-muted-foreground overline mb-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const iso = `${month}-${String(d).padStart(2, "0")}`;
          const weekend = (i % 7) >= 5;
          const holiday = holidays[iso];
          const items = forDay(d);
          return (
            <div key={iso} data-testid={`leave-cal-day-${iso}`} data-holiday={holiday ? "true" : undefined}
                 className={`min-h-16 rounded border p-1 text-[10px] ${holiday ? "bg-red-50 border-red-200" : weekend ? "bg-slate-50 border-slate-100" : "border-border"} ${iso === todayIso ? "ring-2 ring-primary/60" : ""}`}>
              <div className={`mono ${iso === todayIso ? "font-bold text-primary" : "text-muted-foreground"}`}>{d}</div>
              {holiday && <div className="truncate text-red-700 font-semibold leading-tight" title={holiday}>{holiday}</div>}
              <div className="space-y-0.5 mt-0.5">
                {items.slice(0, 3).map((r) => (
                  <div key={r.id} title={`${r.employee_name} — ${r.type.replace("_", " ")} (${r.status})`}
                       className={`truncate rounded px-1 border ${TYPE_TONE[r.type] || TYPE_TONE.other} ${r.status === "pending" ? "border-dashed opacity-70" : ""}`}>
                    {r.employee_name.split(" ")[0]}{r.half_day ? " ½" : ""}
                  </div>
                ))}
                {items.length > 3 && <div className="text-muted-foreground">+{items.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground mt-2">Solid = approved · dashed = pending · red days = public holidays. Colours: annual green, sick red, maternity/paternity pink, unpaid grey, compassionate violet.</p>
    </div>
  );
}
